/**
 * Essentials to add — add-on products with a one-tap quick add.
 *
 * Three jobs, in order:
 *
 *   1. Fetch. Asks Shopify's recommendations endpoint for this product's
 *      complementary products. The response is this same section rendered
 *      again server-side, so the cards come back as finished markup — this
 *      file never builds a card. It lifts out the results div carrying its own
 *      block id, which is what lets two recommendation blocks live on the same
 *      product without swapping results. Nothing usable back, or the request
 *      failed: the block takes itself off the page. In the theme editor it
 *      stays and explains itself instead, so the block stays selectable.
 *
 *   2. Cards. Changing a card's variant control repoints its price and its
 *      Add button from the variant JSON that came down with the card — no
 *      second request per card. Submitting posts to theme.routes.cartAdd and
 *      dispatches `ajaxProduct:added` on document, which is the event theme.js
 *      CartDrawer listens for to rebuild and open the drawer. Every card is a
 *      real form whose variant control is named "id", so with this file absent
 *      the same click still adds the same variant, the slow way.
 *
 *   3. Arrows. They only page the CSS scroll-snap column up and down. They
 *      stay hidden unless the list actually overflows, and the column scrolls
 *      perfectly well without them.
 */
(function () {
  'use strict';

  if (!('customElements' in window) || !window.fetch || !window.DOMParser) return;
  if (customElements.get('product-upsell')) return;

  var ADDED_MS = 1600;
  var REDUCED =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function designMode() {
    return !!(window.Shopify && window.Shopify.designMode);
  }

  function cssEscape(value) {
    var str = String(value);
    if (window.CSS && CSS.escape) return CSS.escape(str);
    return str.replace(/["\\\]]/g, '\\$&');
  }

  function money(cents) {
    try {
      if (window.theme && theme.Currency && theme.Currency.formatMoney) {
        return theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
      }
    } catch (e) {
      /* fall through to the plain number below */
    }
    return (Number(cents) / 100).toFixed(2);
  }

  function cartAddUrl() {
    if (window.theme && theme.routes && theme.routes.cartAdd) return theme.routes.cartAdd;
    return '/cart/add.js';
  }

  class ProductUpsell extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;

      this.uid = this.dataset.blockId || '';
      this.url = this.dataset.url || '';
      this.list = this.querySelector('[data-upsell-list]');
      this.viewport = this.querySelector('[data-upsell-viewport]');
      this.nav = this.querySelector('[data-upsell-nav]');
      this.statusEl = this.querySelector('[data-upsell-status]');
      this.emptyEl = this.querySelector('[data-upsell-empty]');
      this.timers = [];

      // Material matches (boot to bag, bag to boot) are printed by Liquid
      // straight into the list, so there is nothing to fetch.
      if (
        this.dataset.prerendered === 'true' &&
        this.list &&
        this.list.querySelector('form.product-upsell__card')
      ) {
        this.ready();
        return;
      }

      if (!this.url || !this.list) {
        this.bail();
        return;
      }

      this.load();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      this.timers.forEach(clearTimeout);
      this.timers = [];
    }

    /* --- 1. Fetch --------------------------------------------------------- */

    load() {
      var self = this;

      fetch(this.url, { credentials: 'same-origin' })
        .then(function (response) {
          if (!response.ok) throw new Error('Recommendations request failed');
          return response.text();
        })
        .then(function (text) {
          // DOMParser rather than innerHTML: nothing in the parsed document
          // loads, runs or upgrades, so the discarded parts cost nothing.
          var parsed = new DOMParser().parseFromString(text, 'text/html');
          var results =
            parsed.querySelector('[data-upsell-results][data-block-id="' + cssEscape(self.uid) + '"]') ||
            parsed.querySelector('[data-upsell-results]');
          var cards = results ? results.querySelectorAll('form.product-upsell__card') : [];

          if (!cards.length) {
            self.bail();
            return;
          }

          var fragment = document.createDocumentFragment();
          for (var i = 0; i < cards.length; i++) {
            fragment.appendChild(document.importNode(cards[i], true));
          }

          self.list.innerHTML = '';
          self.list.appendChild(fragment);
          self.ready();
        })
        .catch(function () {
          self.bail();
        });
    }

    ready() {
      var self = this;
      var cards = this.list.querySelectorAll('form.product-upsell__card');

      for (var i = 0; i < cards.length; i++) this.bindCard(cards[i]);

      this.classList.add('is-loaded');
      this.setupNav();

      // Deliberately NOT `recommendations:loaded`. The only listener for that
      // event in the theme (assets/theme.js:5620) ignores the detail and
      // re-scans its own <product-recommendations> element, calling
      // colorSwatchHovering() again — which addEventListener's a fresh bound
      // handler per swatch every time, so firing it here would double-bind
      // every swatch in the "Pairs well with" block that loaded before us.
      // Nothing in these cards needs that pass anyway.
      document.dispatchEvent(
        new CustomEvent('upsell:loaded', {
          detail: { block: self, intent: self.dataset.intent || 'complementary', count: cards.length }
        })
      );
    }

    bail() {
      if (designMode()) {
        if (this.list) this.list.innerHTML = '';
        if (this.nav) this.nav.hidden = true;
        if (this.emptyEl) this.emptyEl.hidden = false;
        this.classList.add('is-empty');
        return;
      }

      // The .product-block wrapper product-template.liquid puts around this
      // snippet carries a 25px bottom margin, so an emptied one would leave a
      // visible gap. The wrapper also holds this snippet's own <link> and
      // <script> tags, which render nothing — they do not count as company.
      var host = this.parentElement;
      this.remove();

      if (!host || !host.classList.contains('product-block')) return;

      var kids = host.children;
      for (var i = 0; i < kids.length; i++) {
        var tag = kids[i].tagName;
        if (tag !== 'LINK' && tag !== 'SCRIPT' && tag !== 'STYLE' && tag !== 'TEMPLATE') return;
      }
      host.remove();
    }

    /* --- 2. Cards --------------------------------------------------------- */

    bindCard(card) {
      var self = this;
      var button = card.querySelector('[data-upsell-add]');
      var label = card.querySelector('[data-upsell-add-label]');
      var priceNow = card.querySelector('[data-upsell-price-current]');
      var priceWas = card.querySelector('[data-upsell-price-was]');
      var dataEl = card.querySelector('[data-upsell-variants]');
      var variants = null;

      if (dataEl) {
        try {
          variants = JSON.parse(dataEl.textContent);
        } catch (e) {
          variants = null;
        }
      }

      function selectedId() {
        var select = card.querySelector('select[name="id"]');
        if (select) return select.value;
        var checked = card.querySelector('input[name="id"]:checked');
        if (checked) return checked.value;
        var hidden = card.querySelector('input[name="id"]');
        return hidden ? hidden.value : null;
      }

      function variantById(id) {
        if (!variants || !id) return null;
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === String(id)) return variants[i];
        }
        return null;
      }

      function sync() {
        var variant = variantById(selectedId());
        if (!variant) return;

        if (priceNow) priceNow.textContent = money(variant.price);

        if (priceWas) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            priceWas.textContent = money(variant.compare_at_price);
            priceWas.hidden = false;
          } else {
            priceWas.textContent = '';
            priceWas.hidden = true;
          }
        }

        if (button) {
          button.disabled = !variant.available;
          if (label) {
            label.textContent = variant.available
              ? button.dataset.labelAdd || label.textContent
              : button.dataset.labelSoldout || label.textContent;
          }
        }
      }

      function flash(text, className) {
        if (!button || !label) return;
        button.classList.add(className);
        label.textContent = text;
        var timer = setTimeout(function () {
          button.classList.remove(className);
          // Back to whatever the currently selected variant says now — it may
          // have been the last one in stock.
          if (variants) {
            sync();
          } else {
            label.textContent = button.dataset.labelAdd || text;
          }
        }, ADDED_MS);
        self.timers.push(timer);
      }

      card.addEventListener(
        'change',
        function (event) {
          if (!event.target.hasAttribute('data-upsell-option')) return;
          sync();
        },
        { passive: true }
      );

      card.addEventListener('submit', function (event) {
        // Taken over unconditionally: bailing out after this point must never
        // fall through to a real page-reloading POST.
        event.preventDefault();
        if (!button || button.disabled || card.dataset.busy === '1') return;

        card.dataset.busy = '1';
        button.classList.add('is-busy');
        self.announce('');

        fetch(cartAddUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/json'
          },
          body: new FormData(card)
        })
          .then(function (response) {
            return response.json().then(function (data) {
              return { ok: response.ok, data: data || {} };
            });
          })
          .then(function (result) {
            var data = result.data;

            // 422 is what the cart returns for "can't add that many" and the
            // like; theme.js reads the same `description` field.
            if (!result.ok || data.status === 422 || data.status === 404 || data.description) {
              self.announce(data.description || data.message || self.dataset.labelError || '');
              button.classList.add('is-error');
              var timer = setTimeout(function () {
                button.classList.remove('is-error');
              }, 600);
              self.timers.push(timer);
              return;
            }

            flash(button.dataset.labelAdded || '', 'is-added');
            self.announce(button.dataset.labelAdded || '');
            var clear = setTimeout(function () {
              self.announce('');
            }, ADDED_MS);
            self.timers.push(clear);

            // Dispatched on the card's own form and left to bubble, exactly as
            // theme.js ProductForm.success does (assets/theme.js:2881). Other
            // listeners in the theme read event.target.querySelector('[name="id"]')
            // off it — firing this on `document` instead hands them whichever
            // [name="id"] happens to come first on the page.
            card.dispatchEvent(
              new CustomEvent('ajaxProduct:added', {
                bubbles: true,
                detail: { product: data, addToCartBtn: button }
              })
            );

            if (window.theme && theme.settings && theme.settings.cartType === 'page' && theme.routes) {
              window.location = theme.routes.cartPage;
            }
          })
          .catch(function () {
            self.announce(self.dataset.labelError || '');
          })
          .then(function () {
            card.dataset.busy = '0';
            button.classList.remove('is-busy');
          });
      });

      sync();
    }

    announce(text) {
      if (this.statusEl) this.statusEl.textContent = text || '';
    }

    /* --- 3. Arrows -------------------------------------------------------- */

    setupNav() {
      if (!this.viewport) return;

      var self = this;
      // The arrows are optional — the merchant can turn them off, and the
      // column still has to scroll and still has to be reachable by keyboard.
      var useArrows = this.nav && this.dataset.arrows === 'true';
      var prev = useArrows ? this.nav.querySelector('[data-upsell-prev]') : null;
      var next = useArrows ? this.nav.querySelector('[data-upsell-next]') : null;
      if (!prev || !next) {
        prev = null;
        next = null;
      }

      // One page is however many whole cards currently fit in the viewport.
      function pageDistance() {
        var card = self.list.querySelector('form.product-upsell__card');
        if (!card) return self.viewport.clientHeight;

        var gap = parseFloat(getComputedStyle(self.list).rowGap) || 0;
        var step = card.getBoundingClientRect().height + gap;
        if (!step) return self.viewport.clientHeight;

        var rows = Math.max(1, Math.round((self.viewport.clientHeight + gap) / step));
        return step * rows;
      }

      function page(direction) {
        self.viewport.scrollBy({
          top: direction * pageDistance(),
          behavior: REDUCED ? 'auto' : 'smooth'
        });
      }

      if (prev && next) {
        prev.addEventListener('click', function () {
          page(-1);
        });
        next.addEventListener('click', function () {
          page(1);
        });
      }

      var pending = false;

      function update() {
        pending = false;
        var max = self.viewport.scrollHeight - self.viewport.clientHeight;
        var scrolls = max > 2;

        if (self.nav) self.nav.hidden = !(scrolls && prev && next);
        if (prev && next) {
          prev.disabled = !scrolls || self.viewport.scrollTop <= 1;
          next.disabled = !scrolls || self.viewport.scrollTop >= max - 1;
        }

        // A scroll container is only reliably reachable by keyboard once it is
        // in the tab order; pointless when there is nothing to scroll.
        if (scrolls) {
          self.viewport.setAttribute('tabindex', '0');
        } else {
          self.viewport.removeAttribute('tabindex');
        }
      }

      function schedule() {
        if (pending) return;
        pending = true;
        requestAnimationFrame(update);
      }

      this.viewport.addEventListener('scroll', schedule, { passive: true });

      // content-visibility means the list may not have been laid out yet; the
      // observer fires the moment it is, and on every later reflow.
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(schedule);
        this.resizeObserver.observe(this.list);
        this.resizeObserver.observe(this.viewport);
      } else {
        this.onResize = schedule;
        window.addEventListener('resize', schedule, { passive: true });
      }

      schedule();
    }
  }

  customElements.define('product-upsell', ProductUpsell);
})();
